import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  InputAdornment,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import {
  ImageField,
  ImageInput,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useDataProvider,
} from "react-admin";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase";
import { useFormContext } from "react-hook-form";
import { ClientHas } from "../Machines/RefreshMachineStatus";
import MROAccessGroupInput from "../Machines/MROAccessGroupInput";
import { doc, setDoc } from "firebase/firestore";
import _extendLayout from "../../layout/_extendLayout";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  productImage: {
    objectFit: "contain",
    width: "100%",
    height: 200,
    maxHeight: 200,
    display: "block",
  },
  imageContainer: {
    minHeight: 200,
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
}));

const creditMustBeZero = (msg) => (value) => {
  const num = Number(value);
  if (num < 0) return "Please enter at least 0";
  const regex = /^\d*(\.\d{0,2})?$/;
  if (!regex.test(String(value))) {
    return (
      msg || "Please enter a valid credit price with no more than two decimals."
    );
  }
};
const CategoryInput = ({ client, defaultValue }) => {
  return (
    <ReferenceInput
      key={client}
      source="category"
      reference="categories"
      filter={{ client: client || "" }}
      filterToQuery={() => {}}
      fullWidth
      perPage={1000}
    >
      <SelectInput
        optionText="name"
        fullWidth
        validate={required("Please select a category.")}
        defaultValue={defaultValue || ""}
      />
    </ReferenceInput>
  );
};

const LimitationRuleInput = ({ client, defaultValue }) => {
  return (
    <ReferenceInput
      key={client}
      source="limitationRule"
      reference="limitationRules"
      filter={{ client: client || "" }}
      filterToQuery={() => {}}
      perPage={1000}
      fullWidth
      resettable={true}
    >
      <SelectInput optionText="name" fullWidth defaultValue={defaultValue} />
    </ReferenceInput>
  );
};

const ProductEdit = () => {
  const { id } = useParams();
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const form = useFormContext();
  const navigate = useNavigate();

  const [proDetails, setProDetails] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [mroAccess, setMroAccess] = useState(proDetails?.mroAccessGroup || []);
  const [limitationRules, setLimitationRules] = useState([]);

  useEffect(() => {
    const checkAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await getCurrentUser(user.uid);
        await getProductById();
      }
    });
    return () => checkAuth();
  }, []);
  const getProductById = async () => {
    try {
      const res = await dataProvider.getOne("products", { id });
      setProDetails(res.data);
    } catch (err) {
      console.log(err);
    }
  };
  const getCurrentUser = async (userId) => {
    try {
      const res = await dataProvider.getOne("users", { id: userId });
      setIsAdmin(res.data.role === "admin");
      setClientId(res.data?.client?.id);
      await getLimitationList(res.data?.client?.id);
    } catch (err) {
      console.log(err);
    }
  };

  const getLimitationList = async (clientId) => {
    try {
      const res = await dataProvider.getList("limitationRules", {
        filter: { client: clientId },
        sort: { field: "createdAt", order: "DESC" },
        pagination: { page: 1, perPage: 1000 },
      });
      const limitationChoices = res.data.map((limitation) => ({
        id: limitation.id,
        name: limitation.name,
      }));
      setLimitationRules(limitationChoices);
    } catch (err) {
      console.log(err);
    }
  };

  const handleClientChange = () => {
    const categoryState = form.getFieldState("category");
    if (categoryState) {
      form.setValue("category", "");
    }

    const mroAccessState = form.getFieldState("mroAccessGroup");
    if (mroAccessState) {
      form.setValue("mroAccessGroup", []);
    }
  };

  const handleMroAccessChange = (updateMroAccess) => {
    setMroAccess(updateMroAccess);
  };
  const handleSave = async (datas) => {
    const file = datas.image?.rawFile;
    let imgUrl = proDetails?.image?.src ?? "";

    if (file instanceof File) {
      try {
        const storage = getStorage();
        const storageRef = ref(storage, `products/${id}/image`);
        const uploadSnapshot = await uploadBytes(storageRef, file);
        imgUrl = await getDownloadURL(uploadSnapshot.ref);
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to upload image. Please try again.");
        return;
      }
    }

    let obj = {
      category: datas.category,
      client: datas.client,
      description: datas.description ?? "",
      image: {
        src: imgUrl ?? "",
      },
      limitationRule: datas.limitationRule ?? null,
      name: datas.name ?? "",
      priceCash: datas.priceCash ?? 0,
      priceCredit: datas.priceCredit ?? 0,
      serialNumber: datas.serialNumber ?? "",
      sku: datas.sku ?? "",
      mroAccessGroup: mroAccess ?? [],
    };

    try {
      const docRef = doc(db, "products", id);
      await setDoc(docRef, obj, { merge: true });
      toast.success("Product saved successfully!");
      navigate("/products");
    } catch (err) {
      console.log("Error saving to Firebase:", err);
      toast.error("Failed to save product. Please try again.");
    }
  };

  if (!proDetails) {
    return (
      <_extendLayout>
        <p>Loading...</p>
      </_extendLayout>
    );
  }
  return (
    <_extendLayout>
      <div className="listMachines">
        <CardHeader title="Edit Product" />
        <CardContent>
          <p>Product Details</p>
          <SimpleForm toolbar={null} onSubmit={handleSave}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography gutterBottom variant="h6">
                  Product Image
                </Typography>
                <div className={classes.imageContainer}>
                  <ImageInput
                    //  key={proDetails?.image?.src || "no-image"}
                    source="image"
                    accept="image/*"
                    label=""
                    fullWidth
                    defaultValue={proDetails?.image ?? ""}
                    multiple={false}
                    options={{ maxFiles: 1 }}
                    placeholder={
                      <div
                        style={{
                          padding: 24,
                          textAlign: "center",
                          color: "#777",
                        }}
                      >
                        Drag & drop an image here, or click to select
                      </div>
                    }
                  >
                    <ImageField
                      source="src"
                      classes={{ image: classes.productImage }}
                    />
                  </ImageInput>
                </div>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: "8px" }}
                >
                  Recommended size: 720 x 720 px. Display ratio 1:1 png or jpg
                </Typography>
              </Grid>

              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextInput
                      label="Product Name"
                      source="name"
                      fullWidth
                      validate={required("Please enter a name.")}
                      defaultValue={proDetails.name ?? ""}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      label="Description"
                      source="description"
                      fullWidth
                      defaultValue={proDetails.description ?? ""}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="SKU"
                      source="sku"
                      fullWidth
                      defaultValue={proDetails.sku ?? ""}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="Serial Number"
                      source="serialNumber"
                      fullWidth
                      defaultValue={proDetails.serialNumber ?? ""}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {isAdmin ? (
                      <ReferenceInput
                        source="client"
                        reference="clients"
                        filterToQuery={() => {}}
                        onChange={handleClientChange}
                      >
                        <SelectInput
                          optionText="companyName"
                          validate={required("Please select a client.")}
                          fullWidth
                          defaultValue={proDetails.client}
                        />
                      </ReferenceInput>
                    ) : (
                      <TextInput
                        source="client"
                        label="Client"
                        fullWidth
                        defaultValue={clientId}
                        disabled
                        type="hidden"
                        style={{ display: "none" }}
                      />
                    )}
                  </Grid>

                  {proDetails.client && (
                    <>
                      <Grid item xs={12}>
                        <CategoryInput
                          client={proDetails.client || clientId}
                          defaultValue={proDetails.category}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <SelectInput
                          source="limitationRule"
                          label="Limitation rule"
                          isRequired
                          choices={limitationRules}
                          fullWidth
                          defaultValue={proDetails.limitationRule}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} md={6}>
                    <NumberInput
                      source="priceCash"
                      label="Price"
                      fullWidth
                      defaultValue={proDetails.priceCash ?? ""}
                      validate={[
                        required("Please enter a price"),
                        creditMustBeZero(
                          "Please enter a valid price with no more than two decimals.",
                        ),
                      ]}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">RM</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {proDetails.client && (
                    <ClientHas
                      client={proDetails.client}
                      module="mro"
                      fallback={null}
                    >
                      <>
                        <Grid item xs={12} md={6}>
                          <NumberInput
                            source="priceCredit"
                            label="Price (Credit)"
                            fullWidth
                            defaultValue={proDetails.priceCredit ?? ""}
                            validate={[
                              required("Please enter credit price"),
                              creditMustBeZero(
                                "Please enter a valid credit price with no more than two decimals.",
                              ),
                            ]}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <MROAccessGroupInput
                            client={proDetails.client}
                            value={proDetails.mroAccessGroup || []}
                            initialValue={proDetails.mroAccessGroup || []}
                            onValueChange={handleMroAccessChange}
                          />
                        </Grid>
                      </>
                    </ClientHas>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <CardActions>
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
              <Button onClick={() => navigate(`/products`)} variant="contained">
                Back
              </Button>
            </CardActions>
          </SimpleForm>
        </CardContent>
      </div>
    </_extendLayout>
  );
};

export default ProductEdit;
